import { Container } from './Container'
import React, { useState, useEffect } from 'react';
import { Button } from './Buttons';
import logo from '../../assets/images/logo.jpeg';

export function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Define the animation styles
  const animationStyles = `
    @keyframes fadeInOut {
      0%, 45%, 100% { opacity: 1; }
      50%, 95% { opacity: 0; }
    }
    
    @keyframes fadeInOutDelayed {
      0%, 45%, 100% { opacity: 0; }
      50%, 95% { opacity: 1; }
    }
    
    .animate-fade-in-out {
      animation: fadeInOut 5s infinite;
    }
    
    .animate-fade-in-out-delayed {
      animation: fadeInOutDelayed 5s infinite;
    }
  `;

  return (
    <Container className="pt-16 pb-16 lg:pt-28 relative overflow-hidden min-h-[calc(100vh-80px)] flex flex-col">
      {/* Animation styles */}
      <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
      
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Main gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30"></div>
        
        {/* Radial gradient accent - more centered on mobile */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[90vw] max-w-[800px] h-[90vw] max-h-[800px] rounded-full bg-gradient-to-r from-blue-400/5 to-indigo-500/5 blur-3xl"></div>
        
        {/* Modern grid lines */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid-animation"></div>
        </div>
        
        {/* Floating elements - adjusted for better mobile appearance */}
        <div className="absolute top-32 left-[10%] w-4 h-4 rounded-full bg-blue-500/20"></div>
        <div className="absolute top-48 right-[10%] w-6 h-6 rounded-full bg-indigo-500/20"></div>
        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-blue-400/20 md:left-1/4 md:translate-x-0"></div>
      </div>
      
      {/* Hero Content */}
      <div className="w-full mx-auto flex-grow flex flex-col">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between h-full pt-4 lg:pt-16">
          {/* Left side with text content */}
          <div className="w-full lg:w-5/12 z-10 text-left">
            <div 
              className={`transform transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Main heading with redesigned styling */}
              <h1 className="mb-6">
                <div className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent inline-block">AI-Powered Audits,</span>
                  <span className="text-slate-800 block lg:inline text-3xl md:text-4xl lg:text-5xl"> Less Work, Zero Surprises</span>
                </div>
              </h1>
              
              <p className="text-lg text-slate-700 leading-relaxed mb-8 max-w-2xl">
                AI-driven audit management and compliance from planning to reporting - <span className="text-blue-600 font-medium">no gaps</span>, <span className="text-indigo-600 font-medium">no guesswork</span>.
              </p>
              
              {/* CTA Buttons and Trust Badge - Horizontal Layout */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
                {/* CTA Button */}
                <Button 
                  href="/register"
                  color="blue"
                  className="premium-cta shine-effect relative w-full sm:w-auto text-center py-4 px-10 sm:px-14 text-lg font-semibold tracking-wide rounded-lg overflow-hidden transition-all duration-500 backdrop-blur-sm"
                >
                  <div className="relative z-10 flex items-center justify-center">
                    <span className="text-white">BOOK A DEMO</span>
                    <svg className="w-5 h-5 ml-2 transform transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </Button>
                
                {/* Minimalist Trust Badge - Hidden on mobile, visible on sm breakpoint and above */}
                <div className="hidden sm:flex items-center bg-white/90 border border-indigo-100 rounded-lg px-4 py-2.5 shadow-sm">
                  <div className="mr-3 flex-shrink-0">
                    <div className="h-7 w-7 rounded-full bg-indigo-50 flex items-center justify-center">
                      <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Backed by Early Adopters</div>
                    <div className="flex items-center">
                      <div className="flex space-x-0.5 mr-1.5">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-xs font-medium text-slate-600">10+ innovative teams</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Feature badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="flex items-center bg-white/80 border border-blue-100 px-3 py-1.5 rounded-lg shadow-sm">
                  <svg className="w-4 h-4 text-blue-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-sm font-medium text-slate-700">AI-Powered</span>
                </div>
                <div className="flex items-center bg-white/80 border border-blue-100 px-3 py-1.5 rounded-lg shadow-sm">
                  <svg className="w-4 h-4 text-indigo-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-sm font-medium text-slate-700">Enterprise Security</span>
                </div>
                <div className="flex items-center bg-white/80 border border-blue-100 px-3 py-1.5 rounded-lg shadow-sm">
                  <svg className="w-4 h-4 text-purple-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-sm font-medium text-slate-700">Real-time Analytics</span>
                </div>
              </div>
              
              {/* Premium button & trust badge styles */}
              <style jsx>{`
                .premium-cta {
                  background: linear-gradient(135deg, rgba(37, 99, 235, 0.9), rgba(79, 70, 229, 0.9));
                  box-shadow: 
                    0 10px 25px -3px rgba(37, 99, 235, 0.25),
                    0 4px 6px -2px rgba(37, 99, 235, 0.05),
                    0 0 0 2px rgba(255, 255, 255, 0.1) inset,
                    0 5px 10px rgba(0, 0, 0, 0.2);
                  transform-style: preserve-3d;
                  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                  letter-spacing: 0.5px;
                  border: 1px solid rgba(255, 255, 255, 0.2);
                }
                
                /* Shining effect */
                .shine-effect::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: -150%;
                  width: 100%;
                  height: 100%;
                  background: linear-gradient(
                    to right,
                    rgba(255, 255, 255, 0) 0%,
                    rgba(255, 255, 255, 0.3) 50%,
                    rgba(255, 255, 255, 0) 100%
                  );
                  transform: skewX(-25deg);
                  animation: shine 3s infinite;
                  z-index: 1;
                }
                
                @keyframes shine {
                  0% {
                    left: -150%;
                  }
                  60% {
                    left: -150%;
                  }
                  100% {
                    left: 150%;
                  }
                }
                
                .premium-cta::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  background: linear-gradient(
                    135deg,
                    rgba(255, 255, 255, 0.25) 0%,
                    rgba(255, 255, 255, 0) 60%
                  );
                  z-index: 1;
                }
                
                .premium-cta::after {
                  content: '';
                  position: absolute;
                  top: -2px;
                  left: -2px;
                  right: -2px;
                  bottom: -2px;
                  background: linear-gradient(45deg, #3b82f6, #6366f1, #8b5cf6, #3b82f6);
                  background-size: 400% 400%;
                  z-index: -1;
                  border-radius: inherit;
                  animation: gradient-border 6s ease infinite;
                  filter: blur(4px);
                  opacity: 0.7;
                }
                
                .premium-cta:hover {
                  transform: translateY(-3px) scale(1.02);
                  box-shadow: 
                    0 20px 25px -5px rgba(37, 99, 235, 0.3),
                    0 10px 10px -5px rgba(37, 99, 235, 0.1),
                    0 0 0 2px rgba(255, 255, 255, 0.15) inset,
                    0 8px 15px rgba(0, 0, 0, 0.2);
                }
                
                .premium-cta:active {
                  transform: translateY(-1px) scale(0.99);
                  transition-duration: 0.1s;
                }

                .premium-trust-badge {
                  box-shadow: 
                    0 4px 6px -1px rgba(0, 0, 0, 0.1),
                    0 2px 4px -1px rgba(0, 0, 0, 0.06),
                    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
                  transform: translateZ(0);
                }
                
                @keyframes gradient-border {
                  0% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                  100% { background-position: 0% 50%; }
                }
                
                @keyframes pulse-subtle {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4); }
                  50% { box-shadow: 0 0 0 8px rgba(79, 70, 229, 0); }
                }
                
                @keyframes pulse-fast {
                  0%, 100% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.7; transform: scale(0.9); }
                }
                .animate-pulse-fast {
                  animation: pulse-fast 1.5s infinite;
                }
              `}</style>
            </div>
          </div>
        
          {/* Right side with solution showcase - full width on mobile, proper alignment on desktop */}
          <div className="w-full lg:w-6/12 mt-4 lg:mt-0 relative z-10 lg:pl-6">
            <div 
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Premium dashboard mockup */}
              <div className="relative w-full max-w-xl mx-auto">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-2xl transform rotate-2 scale-[1.03] blur-[2px]"></div>
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* Header bar */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400 mr-2"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400 mr-2"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400 mr-2"></div>
                      </div>
                      <div className="text-white text-sm font-medium hidden xs:block">AuditAI Dashboard</div>
                      <div className="flex items-center space-x-1">
                        <div className="px-2 py-1 rounded bg-blue-500/20 text-white text-xs font-medium">AI-Powered</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dashboard content */}
                  <div className="p-4 pt-3">
                    {/* Redesigned Transformations with Flip Animation - Moved to top */}
                    <div className="mb-4">
                      <h3 className="text-slate-700 font-medium mb-2">Transform your compliance workflow</h3>
                      <div className="h-1 w-16 bg-blue-500 rounded-full mb-3"></div>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center">
                        <svg className="w-4 h-4 text-blue-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        See the transformation
                      </h4>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {/* Card 1: Auto-Flip Animation */}
                        <div className="flip-card h-28 perspective-1000">
                          <div className="flip-card-inner relative w-full h-full transition-transform duration-1000 transform-style-3d animate-flip">
                            {/* Front Side (Before) */}
                            <div className="flip-card-front absolute w-full h-full backface-hidden rounded-lg border border-red-100 bg-white p-3">
                              <div className="flex items-center h-full">
                                <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center mr-2 shrink-0">
                                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs text-red-500 uppercase font-medium mb-1">Before</div>
                                  <div className="text-sm font-medium text-slate-700">Manual Evidence Collection</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Back Side (After) */}
                            <div className="flip-card-back absolute w-full h-full backface-hidden rounded-lg border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 p-3 rotate-y-180">
                              <div className="flex items-center h-full">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mr-2 shrink-0 shadow-sm">
                                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs text-emerald-600 uppercase font-medium mb-1">After</div>
                                  <div className="text-sm font-medium text-slate-700">Automated Digital Evidence</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Card 2: Auto-Flip Animation with Delay */}
                        <div className="flip-card h-28 perspective-1000">
                          <div className="flip-card-inner relative w-full h-full transition-transform duration-1000 transform-style-3d animate-flip-delayed">
                            {/* Front Side (Before) */}
                            <div className="flip-card-front absolute w-full h-full backface-hidden rounded-lg border border-red-100 bg-white p-3">
                              <div className="flex items-center h-full">
                                <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center mr-2 shrink-0">
                                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs text-red-500 uppercase font-medium mb-1">Before</div>
                                  <div className="text-sm font-medium text-slate-700">Control Gaps & Risks</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Back Side (After) */}
                            <div className="flip-card-back absolute w-full h-full backface-hidden rounded-lg border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 p-3 rotate-y-180">
                              <div className="flex items-center h-full">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mr-2 shrink-0 shadow-sm">
                                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs text-emerald-600 uppercase font-medium mb-1">After</div>
                                  <div className="text-sm font-medium text-slate-700">100% Controls Coverage</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Card 3: Auto-Flip Animation with More Delay */}
                        <div className="flip-card h-28 perspective-1000">
                          <div className="flip-card-inner relative w-full h-full transition-transform duration-1000 transform-style-3d animate-flip-more-delayed">
                            {/* Front Side (Before) */}
                            <div className="flip-card-front absolute w-full h-full backface-hidden rounded-lg border border-red-100 bg-white p-3">
                              <div className="flex items-center h-full">
                                <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center mr-2 shrink-0">
                                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs text-red-500 uppercase font-medium mb-1">Before</div>
                                  <div className="text-sm font-medium text-slate-700">Months of Preparation</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Back Side (After) */}
                            <div className="flip-card-back absolute w-full h-full backface-hidden rounded-lg border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 p-3 rotate-y-180">
                              <div className="flex items-center h-full">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mr-2 shrink-0 shadow-sm">
                                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                                  </svg>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs text-emerald-600 uppercase font-medium mb-1">After</div>
                                  <div className="text-sm font-medium text-slate-700">Audit-Ready in Weeks</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Title section moved after transformations */}
                   
                    {/* Add additional CSS for flip animation */}
                    <style jsx>{`
                      .perspective-1000 {
                        perspective: 1000px;
                      }
                      
                      .transform-style-3d {
                        transform-style: preserve-3d;
                      }
                      
                      .backface-hidden {
                        backface-visibility: hidden;
                      }
                      
                      .rotate-y-180 {
                        transform: rotateY(180deg);
                      }
                      
                      @keyframes flip {
                        0%, 45% {
                          transform: rotateY(0deg);
                        }
                        50%, 95% {
                          transform: rotateY(180deg);
                        }
                        100% {
                          transform: rotateY(0deg);
                        }
                      }
                      
                      @keyframes flipDelayed {
                        0%, 50% {
                          transform: rotateY(0deg);
                        }
                        55%, 95% {
                          transform: rotateY(180deg);
                        }
                        100% {
                          transform: rotateY(0deg);
                        }
                      }
                      
                      @keyframes flipMoreDelayed {
                        0%, 60% {
                          transform: rotateY(0deg);
                        }
                        65%, 95% {
                          transform: rotateY(180deg);
                        }
                        100% {
                          transform: rotateY(0deg);
                        }
                      }
                      
                      .animate-flip {
                        animation: flip 8s infinite;
                      }
                      
                      .animate-flip-delayed {
                        animation: flipDelayed 8s infinite;
                      }
                      
                      .animate-flip-more-delayed {
                        animation: flipMoreDelayed 8s infinite;
                      }
                    `}</style>
                    
                    {/* Framework compatibility - smaller and more compact */}
                    <div className="bg-white rounded-lg border border-blue-100 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between shadow-sm">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <svg className="w-4 h-4 text-blue-500 mr-1.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span className="text-xs font-medium text-slate-700">Framework Compatible</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="px-1.5 py-0.5 bg-blue-50 rounded text-xs font-medium text-blue-700">SOX</span>
                        <span className="px-1.5 py-0.5 bg-indigo-50 rounded text-xs font-medium text-indigo-700">SOC</span>
                        <span className="px-1.5 py-0.5 bg-purple-50 rounded text-xs font-medium text-purple-700">NIST</span>
                        <span className="px-1.5 py-0.5 bg-slate-50 rounded text-xs font-medium text-slate-700">+</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer - smaller and more mobile-friendly */}
                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse"></div>
                        <span className="text-xs font-medium text-slate-600">Smart compliance automation</span>
                      </div>
                      <a 
                        href="/register" 
                        className="px-3 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-medium rounded-full shadow-sm hover:shadow-md hover:brightness-110 transition-all duration-300"
                      >
                        Register Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom wave and Explore Features button - adjusted for better mobile visibility */}
      <div className="absolute bottom-0 left-0 right-0 h-16 overflow-hidden z-10 mt-auto">
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <a href="#features" className="flex flex-col items-center text-indigo-600 hover:text-indigo-800 transition-colors">
            <span className="text-xs font-medium mb-1">Explore Features</span>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
        </div>
        
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".1" fill="#4f46e5"></path>
          <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".2" fill="#4f46e5"></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" opacity=".3" fill="#4f46e5"></path>
        </svg>
      </div>
    </Container>
  );
}